// src/layout/components/ingest/IngestActivityChart.jsx
import React, { useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ReferenceArea,
} from "recharts";

const BR_TZ = "America/Sao_Paulo";

function tooltipFormatter(value, name, props) {
  if (value == null) return ["—", name];

  const n = Number(value);
  if (!Number.isFinite(n)) return ["—", name];

  // identifica a série pelo dataKey
  const key = props?.dataKey;

  // % vs ativados
  if (key === "seen_ytd_pct_of_activated") {
    return [`${n.toFixed(1)}%`, name];
  }

  // padrão (ex: Vistos YTD)
  return [Math.round(n).toLocaleString("pt-BR"), name];
}

function formatCompact(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  const a = Math.abs(x);

  if (a >= 1_000_000) {
    const v = x / 1_000_000;
    const digits = Math.abs(v) >= 10 ? 0 : 1;
    return `${v.toFixed(digits)}M`;
  }
  if (a >= 1_000) {
    const v = x / 1_000;
    const digits = Math.abs(v) >= 10 ? 0 : 1;
    return `${v.toFixed(digits)}k`;
  }
  if (Number.isInteger(x)) return String(x);
  return x.toFixed(1);
}

function fallbackBucketToMs(bucket, range) {
  if (!bucket) return null;
  const s = String(bucket);

  try {
    if (range === "day") {
      const ymd = new Intl.DateTimeFormat("en-CA", {
        timeZone: BR_TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
      const d = new Date(`${ymd}T${s}:00-03:00`);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }

    if (range === "week") {
      const d = new Date(s.replace(" ", "T") + "-03:00");
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }

    if (s.length === 10) {
      const d = new Date(`${s}T12:00:00-03:00`);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }
  } catch {}

  return null;
}

function pick(obj, ...keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null) return v;
  }
  return null;
}

function defaultSeriesA(metric) {
  const isFlights = metric === "flights";
  return {
    key: metric,
    name: isFlights ? "Voos" : "Drones",
    variant: "bar",
    axis: "left",
    // cor/fill vêm de colors no runtime
  };
}

export default function IngestActivityChart({
  series = [],
  offset = 0,
  nowTick = Date.now(),
  range = "day",
  theme,
  colors,
  windowStartMs,
  windowEndMs,

  /**
   * ✅ modo novo: 2 séries configuráveis
   * each: { key, name, variant: "bar"|"line", axis: "left"|"right", color?, fill?, showDots? }
   */
  seriesA, // opcional
  seriesB, // opcional

  /**
   * ✅ modo antigo (compat): se você passar metric/variant, vira seriesA automaticamente
   */
  metric, // "flights" | "drones"
  metricName,
  variant, // "bar" | "line"

  // layout
  hideYTicks = true,
  hideRightAxis = true,
}) {
  // --- compat: se metric veio, monta seriesA ---
  const sA = useMemo(() => {
    if (seriesA) return seriesA;
    if (metric) {
      const base = defaultSeriesA(metric);
      return {
        ...base,
        name: metricName ?? base.name,
        variant: variant ?? base.variant,
      };
    }
    // default: voos barra
    return { ...defaultSeriesA("flights") };
  }, [seriesA, metric, metricName, variant]);

  const sB = seriesB ?? null;

  // 1) normaliza dados (bucket_ms)
  const data = useMemo(() => {
    const arr = Array.isArray(series) ? series : [];
    return arr.map((r) => {
      const raw = r?.bucket_ms ?? r?.bucket_start_utc_ms;
      let ms = raw == null ? null : Number(raw);
      if (!Number.isFinite(ms)) ms = null;
      if (ms == null) ms = fallbackBucketToMs(r?.bucket, range);
      return { ...r, bucket_ms: ms };
    });
  }, [series, range]);

  // 2) domínio X
  const xMin = Number.isFinite(windowStartMs) ? Number(windowStartMs) : null;
  const xMax = Number.isFinite(windowEndMs) ? Number(windowEndMs) : null;

  const xAxisDomain = useMemo(() => {
    if (xMin == null || xMax == null) return ["dataMin", "dataMax"];
    return [xMin, xMax];
  }, [xMin, xMax]);

  // 3) agora
  const nowXRaw = useMemo(() => {
    if (offset !== 0) return null;
    const n = Date.now();
    return Number.isFinite(n) ? n : null;
  }, [offset, nowTick]);

  const canMark =
    offset === 0 &&
    nowXRaw != null &&
    xMin != null &&
    xMax != null &&
    nowXRaw >= xMin &&
    nowXRaw <= xMax;

  // 4) tick fmt
  const tickFmt = (ms) => {
    const n = Number(ms);
    if (!Number.isFinite(n)) return "";
    const d = new Date(n);

    if (range === "day")
      return d.toLocaleTimeString("pt-BR", {
        timeZone: BR_TZ,
        hour: "2-digit",
        minute: "2-digit",
      });

    if (range === "week")
      return (
        d.toLocaleDateString("pt-BR", { timeZone: BR_TZ, day: "2-digit", month: "2-digit" }) +
        " " +
        d.toLocaleTimeString("pt-BR", { timeZone: BR_TZ, hour: "2-digit" }) +
        "h"
      );

    return d.toLocaleDateString("pt-BR", { timeZone: BR_TZ, day: "2-digit", month: "2-digit" });
  };

  // 5) yMax de cada eixo (pra ReferenceArea preencher legal)
  const yMaxLeft = useMemo(() => {
    const keys = [sA, sB].filter(Boolean).filter((s) => (s.axis ?? "left") === "left").map((s) => s.key);
    const ys = data
      .flatMap((r) => keys.map((k) => Number(r?.[k])))
      .filter((n) => Number.isFinite(n));
    const m = ys.length ? Math.max(...ys) : 0;
    return Math.max(1, m);
  }, [data, sA, sB]);

  const yMaxRight = useMemo(() => {
    const keys = [sA, sB].filter(Boolean).filter((s) => (s.axis ?? "left") === "right").map((s) => s.key);
    const ys = data
      .flatMap((r) => keys.map((k) => Number(r?.[k])))
      .filter((n) => Number.isFinite(n));
    const m = ys.length ? Math.max(...ys) : 0;
    return Math.max(1, m);
  }, [data, sA, sB]);

  const yTickStyle = { fill: "rgba(255,255,255,0.70)", fontSize: 11 };
  const axisLineStyle = { stroke: "rgba(255,255,255,0.18)" };

  const leftAxisWidth = hideYTicks ? 6 : 32;
  const rightAxisWidth = hideRightAxis ? 2 : 32;

  // dots: regra simples (day limita, demais sempre)
  const dotsAllowed = range !== "day" || data.length <= 96;

  const dotFill = theme?.bg ?? theme?.background ?? "rgba(10,15,25,0.92)";
  const activeFill = theme?.red ?? "rgba(228,14,14,0.92)";

  function resolveColor(s) {
    // permite override via s.color/s.fill
    const isFlights = s.key === "flights";
    const lineColor =
      s.color ??
      (isFlights
        ? pick(colors, "flightsLine", "flightsBar")
        : pick(colors, "dronesLine", "dronesBar", "flightsBar"));
    const barFill =
      s.fill ??
      (isFlights
        ? pick(colors, "flightsBar", "flightsLine")
        : pick(colors, "dronesBar", "dronesLine", "flightsBar"));

    return { lineColor, barFill };
  }

  function renderSeries(s, idx) {
    if (!s) return null;
    const axis = s.axis ?? "left";
    const yAxisId = axis === "right" ? "right" : "left";
    const key = s.key;
    const name = s.name ?? key;
    const v = s.variant ?? "bar";
    const { lineColor, barFill } = resolveColor(s);

    const showDots =
      v === "line" && (s.showDots ?? dotsAllowed); // allow override per-series

    if (v === "line") {
      return (
        <Line
          key={`line-${idx}-${key}`}
          yAxisId={yAxisId}
          dataKey={key}
          name={name}
          stroke={lineColor}
          strokeWidth={2}
          isAnimationActive={false}
          type="monotone"
          dot={
            showDots
              ? { r: 2, stroke: lineColor, strokeWidth: 1.5, fill: dotFill }
              : false
          }
          activeDot={{ r: 4, stroke: lineColor, strokeWidth: 2, fill: activeFill }}
        />
      );
    }

    return (
      <Bar
        key={`bar-${idx}-${key}`}
        yAxisId={yAxisId}
        dataKey={key}
        name={name}
        barSize={10}
        fill={barFill}
        isAnimationActive={false}
        radius={[8, 8, 0, 0]}
      />
    );
  }

  // decide se precisamos do eixo direito “de verdade”
  const needRightAxis = Boolean([sA, sB].filter(Boolean).some((s) => (s.axis ?? "left") === "right"));
  const rightAxisVisible = needRightAxis && !hideRightAxis;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 6, right: 2, left: 2, bottom: 2 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.10)" strokeDasharray="3 3" />

        <XAxis
          xAxisId={0}
          dataKey="bucket_ms"
          type="number"
          scale="linear"
          domain={xAxisDomain}
          allowDataOverflow
          tickFormatter={tickFmt}
          tick={{ fill: "rgba(255,255,255,0.70)", fontSize: 12 }}
          axisLine={axisLineStyle}
          tickLine={axisLineStyle}
          padding={{ left: 0, right: 0 }}
          minTickGap={12}
        />

        <YAxis
          yAxisId="left"
          width={leftAxisWidth}
          tick={hideYTicks ? false : yTickStyle}
          axisLine={hideYTicks ? false : axisLineStyle}
          tickLine={hideYTicks ? false : axisLineStyle}
          tickFormatter={hideYTicks ? undefined : formatCompact}
          minTickGap={10}
        />

        <YAxis
          yAxisId="right"
          orientation="right"
          width={needRightAxis ? rightAxisWidth : 2}
          tick={rightAxisVisible ? yTickStyle : false}
          axisLine={rightAxisVisible ? axisLineStyle : false}
          tickLine={rightAxisVisible ? axisLineStyle : false}
          tickFormatter={rightAxisVisible ? formatCompact : undefined}
          minTickGap={10}
        />

        <Tooltip
          formatter={tooltipFormatter}
          labelFormatter={(ms) => tickFmt(ms)}
          contentStyle={{
            background: "rgba(10,15,25,0.92)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 12,
            color: theme?.text ?? "white",
          }}
          labelStyle={{ color: theme?.muted ?? "rgba(255,255,255,0.7)" }}
        />

        <Legend />

        {/* ✅ séries configuráveis */}
        {renderSeries(sA, 0)}
        {renderSeries(sB, 1)}

        {/* ✅ marcações: passado/futuro + linha agora (preenche o eixo dominante) */}
        {canMark && (
          <>
            <ReferenceArea
              xAxisId={0}
              yAxisId="left"
              x1={xMin}
              x2={nowXRaw}
              y1={0}
              y2={yMaxLeft}
              ifOverflow="hidden"
              fill="rgba(255,255,255,0.14)"
              strokeOpacity={0}
              isFront
            />
            <ReferenceArea
              xAxisId={0}
              yAxisId="left"
              x1={nowXRaw}
              x2={xMax}
              y1={0}
              y2={yMaxLeft}
              ifOverflow="hidden"
              fill="rgba(0,0,0,0.18)"
              strokeOpacity={0}
              isFront
            />

            <ReferenceLine
              xAxisId={0}
              yAxisId="left"
              x={nowXRaw}
              ifOverflow="hidden"
              stroke="rgba(228, 14, 14, 0.62)"
              strokeWidth={1.5}
              strokeDasharray="6 6"
              isFront
              label={{
                value: "Agora",
                position: "insideTop",
                fill: "rgba(228, 14, 14,0.92)",
                fontSize: 12,
                fontWeight: 500,
              }}
            />
          </>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
